import {
  Autocomplete,
  DatePicker,
  Input,
  RadioButtonGroup
} from '@fullscreendigital/carrefour-dex-fe'
import { Box, Grid } from '@mui/material'
import { AsyncAutocomplete } from 'components/core-ui/AsyncAutocomplete'
import s from '../ReportModal.modules.scss'
import dayjs from 'dayjs'
import {
  Carrier,
  CarrierOptionType,
  CollectingCompanyTableItem,
  CompanyOptionType,
  DEEEReportFormInterface,
  Delegate,
  DelegateOptionType
} from 'components/waste-management/interfaces'
import { useSelectSearch } from 'hooks/useSelectSearch'
import { getNonNegativeNumberInputHandlers } from '../helpers'

const DangerousDEEEReportForm = (props: DEEEReportFormInterface) => {
  const {
    formik,
    currentCompany,
    operationTypesOptions,
    currentLocationInfo,
    isExternalArticlesReport
  } = props

  const carrierSelectSearch = useSelectSearch<Carrier, CarrierOptionType>({
    url: '/waste-management-system/carrier',
    filterName: 'name',
    optionsMap: item => ({
      ...item,
      label: item?.name,
      value: item?.cif
    }),
    initialSearchText: formik.values?.carrierName ?? ''
  })

  const delegateSelectSearch = useSelectSearch<Delegate, DelegateOptionType>({
    url: '/waste-management-system/delegate',
    filterName: 'name',
    optionsMap: item => ({
      ...item,
      label: item?.name,
      value: item?.cnp
    }),
    initialSearchText: formik.values?.delegateName ?? ''
  })

  const companySelectSearch = useSelectSearch<CollectingCompanyTableItem, CompanyOptionType>({
    url: '/waste-management-system/external-articles/waste-collection-companies',
    filterName: 'companyName',
    optionsMap: item => ({
      ...item,
      label: item?.name,
      value: item?.name
    }),
    initialSearchText: formik.values?.companyName ?? ''
  })

  return (
    <>
      <Grid xs={12} md={6} item>
        <Input
          type="text"
          name="hazardousWasteName"
          label="Denumirea deseurilor periculoase"
          fullWidth
          variant="outlined"
          value={formik.values.hazardousWasteName}
          onChange={event => formik.setFieldValue('hazardousWasteName', event.currentTarget.value)}
          error={!!formik.errors.hazardousWasteName}
          helperText={formik.errors.hazardousWasteName as string}
          required
        />
      </Grid>
      <Grid xs={12} md={6} item>
        <Input
          type="text"
          name="wasteCode"
          label="Cod deseuri periculoase"
          fullWidth
          variant="outlined"
          value={formik.values.wasteCode}
          onChange={event => formik.setFieldValue('wasteCode', event.currentTarget.value)}
          error={!!formik.errors.wasteCode}
          helperText={formik.errors.wasteCode as string}
          required
        />
      </Grid>
      <Grid xs={12} md={6} item>
        <Box style={{ marginTop: '17px', minHeight: '58px' }}>
          <RadioButtonGroup
            label="Deseuri periculoase"
            dotStyle
            name="wasteWeightPerYear"
            onChange={(e, option) => {
              formik.setFieldValue('wasteWeightPerYear', option)
            }}
            value={formik.values.wasteWeightPerYear}
            defaultValue={formik.initialValues.wasteWeightPerYear ? '< 1 t/an' : '> 1 t/a'}
            row
            options={[
              {
                label: '< 1 t/an',
                value: 'false'
              },
              {
                label: '> 1 t/an',
                value: 'true'
              }
            ]}
          />
        </Box>
      </Grid>
      {formik.values.wasteWeightPerYear === 'true' && (
        <Grid xs={12} md={6} item>
          <Input
            type="text"
            name="transportApprovalFormNumber"
            label="Nr. formularului de aprobare al transportului"
            fullWidth
            variant="outlined"
            value={formik.values.transportApprovalFormNumber}
            onChange={event =>
              formik.setFieldValue('transportApprovalFormNumber', event.currentTarget.value)
            }
            error={!!formik.errors.transportApprovalFormNumber}
            helperText={formik.errors.transportApprovalFormNumber as string}
            required
          />
        </Grid>
      )}
      <Grid item xs={12} className={s.title}>
        Expeditor
      </Grid>
      <Grid xs={12} md={6} item>
        <Input
          type="text"
          name="expeditorRegisterNumber"
          label="Nr. de inregistrare al expeditorului"
          fullWidth
          variant="outlined"
          value={currentLocationInfo.registrationNumber}
          onChange={event => {}}
          required
          disabled
        />
      </Grid>
      <Grid xs={12} md={6} item>
        <Input
          type="number"
          name="measuredWeight"
          label="Cantitatea predata (kg)"
          fullWidth
          variant="outlined"
          value={formik.values.measuredWeight}
          {...getNonNegativeNumberInputHandlers(formik.setFieldValue, 'measuredWeight')}
          inputProps={{ min: 0 }}
          error={!!formik.errors.measuredWeight}
          helperText={formik.errors.measuredWeight as string}
          required
        />
      </Grid>
      <Grid xs={12} md={6} item>
        <DatePicker
          label="Data incarcare"
          onChange={value => {}}
          variant="outlined"
          required
          value={dayjs(formik.values.carrierHandoverDate) || dayjs()}
          style={{ width: '100%' }}
          disabled
        />
      </Grid>
      <Grid xs={12} md={12} item>
        <Input
          type="textarea"
          name="expeditorName"
          label="Denumirea societatii, sediul, cod unic de identificare"
          fullWidth
          variant="outlined"
          value={`${currentLocationInfo.completeAddressStore}, CIF ${currentLocationInfo.fiscalCode}`}
          onChange={event => {}}
          required
          disabled
          width="100%"
        />
      </Grid>
      <Grid item xs={12} className={s.title}>
        Transportator
      </Grid>
      <Grid xs={12} md={6} item>
        <Autocomplete
          {...carrierSelectSearch}
          label="Nume"
          variant="outlined"
          freeSolo
          autoComplete={false}
          filterOptions={x => x}
          value={formik.values.carrierName}
          onChange={(event, value) => {
            if (value?.name) {
              formik.setFieldValue('carrierId', value.id)
              formik.setFieldValue('carrierName', value.name)
              formik.setFieldValue('cif', value.cif)
              formik.setFieldValue('carrierTradeRegisterNumber', value.tradeRegisterNumber)
              formik.setFieldValue('headquarter', value.headquarter)
            } else {
              formik.setFieldValue('carrierId', '')
              formik.setFieldValue('carrierName', value)
              formik.setFieldValue('cif', '')
              formik.setFieldValue('carrierTradeRegisterNumber', '')
              formik.setFieldValue('headquarter', '')
            }
          }}
          onClose={event => {
            if ((event.target as HTMLInputElement).tagName.toUpperCase() === 'INPUT') {
              formik.setFieldValue('carrierName', (event.target as HTMLInputElement).value)
              formik.setFieldValue('carrierId', '')
              formik.setFieldValue('cif', '')
              formik.setFieldValue('carrierTradeRegisterNumber', '')
              formik.setFieldValue('headquarter', '')
            }
          }}
          error={!!formik.errors.carrierName}
          helperText={formik.errors.carrierName as string}
          required
        />
      </Grid>
      <Grid xs={12} md={6} item>
        <Input
          type="text"
          name="carrierTradeRegisterNumber"
          label="Nr. de inregistrare"
          fullWidth
          variant="outlined"
          value={formik.values.carrierTradeRegisterNumber}
          onChange={event =>
            formik.setFieldValue('carrierTradeRegisterNumber', event.currentTarget.value)
          }
          error={!!formik.errors.carrierTradeRegisterNumber}
          helperText={formik.errors.carrierTradeRegisterNumber as string}
          required
        />
      </Grid>
      <Grid xs={12} md={6} item>
        <Input
          type="text"
          name="cif"
          label="CIF"
          fullWidth
          variant="outlined"
          value={formik.values.cif}
          onChange={event => formik.setFieldValue('cif', event.currentTarget.value)}
          error={!!formik.errors.cif}
          helperText={formik.errors.cif as string}
          required
        />
      </Grid>
      <Grid xs={12} md={12} item>
        <Input
          type="textarea"
          name="headquarter"
          label="Adresa"
          fullWidth
          variant="outlined"
          value={formik.values.headquarter}
          onChange={event => formik.setFieldValue('headquarter', event.currentTarget.value)}
          error={!!formik.errors.headquarter}
          helperText={formik.errors.headquarter as string}
          required
          width="100%"
          rows={2}
        />
      </Grid>
      <Grid xs={12} md={6} item>
        <Autocomplete
          {...delegateSelectSearch}
          label="Nume delegat"
          variant="outlined"
          freeSolo
          autoComplete={false}
          filterOptions={x => x}
          value={formik.values.delegateName}
          onChange={(event, value) => {
            if (value?.name) {
              formik.setFieldValue('delegateId', value.id)
              formik.setFieldValue('delegateName', value.name)
              formik.setFieldValue('vehicleNumber', value.vehicleNumber)
            } else {
              formik.setFieldValue('delegateId', '')
              formik.setFieldValue('delegateName', value)
              formik.setFieldValue('vehicleNumber', '')
            }
          }}
          onClose={event => {
            if ((event.target as HTMLInputElement).tagName.toUpperCase() === 'INPUT') {
              formik.setFieldValue('delegateName', (event.target as HTMLInputElement).value)
              formik.setFieldValue('delegateId', '')
              formik.setFieldValue('vehicleNumber', '')
            }
          }}
          error={!!formik.errors.delegateName}
          helperText={formik.errors.delegateName as string}
          required
        />
      </Grid>
      <Grid xs={12} md={6} item>
        <Input
          type="text"
          name="vehicleNumber"
          label="Nr. de inmatriculare mijloc transport"
          fullWidth
          variant="outlined"
          value={formik.values.vehicleNumber}
          onChange={event => formik.setFieldValue('vehicleNumber', event.currentTarget.value)}
          error={!!formik.errors.vehicleNumber}
          helperText={formik.errors.vehicleNumber as string}
          required
        />
      </Grid>
      <Grid xs={12} md={6} item>
        <Input
          type="number"
          name="carrierReceivedWeight"
          label="Cantitatea primita (kg)"
          fullWidth
          variant="outlined"
          value={formik.values.carrierReceivedWeight}
          {...getNonNegativeNumberInputHandlers(formik.setFieldValue, 'carrierReceivedWeight')}
          inputProps={{ min: 0 }}
          error={!!formik.errors.carrierReceivedWeight}
          helperText={formik.errors.carrierReceivedWeight as string}
          required
        />
      </Grid>
      <Grid xs={12} md={6} item>
        <DatePicker
          label="Data incarcare"
          onChange={value => {}}
          variant="outlined"
          required
          value={dayjs(formik.values.carrierHandoverDate) || dayjs()}
          style={{ width: '100%' }}
          disabled
        />
      </Grid>
      <Grid item xs={12} className={s.title}>
        Destinatar
      </Grid>
      {isExternalArticlesReport ? (
        <>
          <Grid xs={12} md={12} item>
            <Autocomplete
              {...companySelectSearch}
              label="Nume destinatar"
              variant="outlined"
              freeSolo
              autoComplete={false}
              filterOptions={x => x}
              value={formik.values.companyName}
              onChange={(event, value) => {
                if (value?.name) {
                  formik.setFieldValue('companyId', value.id)
                  formik.setFieldValue('companyName', value.name)
                  formik.setFieldValue('companyHeadquarter', value.headquarter)
                  formik.setFieldValue('companyCif', value.cif)
                  formik.setFieldValue('companyTradeRegisterNumber', value.tradeRegisterNumber)
                  formik.setFieldValue('companyCounty', value.county)
                } else {
                  formik.setFieldValue('companyId', '')
                  formik.setFieldValue('companyName', value)
                  formik.setFieldValue('companyHeadquarter', '')
                  formik.setFieldValue('companyCif', '')
                  formik.setFieldValue('companyTradeRegisterNumber', '')
                  formik.setFieldValue('companyCounty', '')
                }
              }}
              onClose={event => {
                if ((event.target as HTMLInputElement).tagName.toUpperCase() === 'INPUT') {
                  formik.setFieldValue('companyName', (event.target as HTMLInputElement).value)
                  formik.setFieldValue('companyId', '')
                  formik.setFieldValue('companyHeadquarter', '')
                  formik.setFieldValue('companyCif', '')
                  formik.setFieldValue('companyTradeRegisterNumber', '')
                  formik.setFieldValue('companyCounty', '')
                }
              }}
              error={!!formik.errors.companyName}
              helperText={formik.errors.companyName as string}
              required
            />
          </Grid>
          <Grid xs={12} md={6} item>
            <Input
              type="text"
              name="companyTradeRegisterNumber"
              label="Numar registrul comertului"
              fullWidth
              variant="outlined"
              value={formik.values.companyTradeRegisterNumber}
              onChange={event =>
                formik.setFieldValue('companyTradeRegisterNumber', event.currentTarget.value)
              }
              error={!!formik.errors.companyTradeRegisterNumber}
              helperText={formik.errors.companyTradeRegisterNumber as string}
              disabled={!!formik.values.companyId}
            />
          </Grid>
          <Grid xs={12} md={6} item>
            <Input
              type="text"
              name="companyCif"
              label="CIF"
              fullWidth
              variant="outlined"
              value={formik.values.companyCif}
              onChange={event => formik.setFieldValue('companyCif', event.currentTarget.value)}
              error={!!formik.errors.companyCif}
              helperText={formik.errors.companyCif as string}
              required
              disabled={!!formik.values.companyId}
            />
          </Grid>
          <Grid xs={12} md={6} item>
            <Input
              type="text"
              name="companyHeadquarter"
              label="Sediul central"
              fullWidth
              variant="outlined"
              value={formik.values.companyHeadquarter}
              onChange={event =>
                formik.setFieldValue('companyHeadquarter', event.currentTarget.value)
              }
              error={!!formik.errors.companyHeadquarter}
              helperText={formik.errors.companyHeadquarter as string}
              disabled={!!formik.values.companyId}
            />
          </Grid>
          <Grid xs={12} md={6} item>
            <Input
              type="text"
              name="companyCounty"
              label="Judet"
              fullWidth
              variant="outlined"
              value={formik.values.companyCounty}
              onChange={event => formik.setFieldValue('companyCounty', event.currentTarget.value)}
              error={!!formik.errors.companyCounty}
              helperText={formik.errors.companyCounty as string}
              disabled={!!formik.values.companyId}
            />
          </Grid>
        </>
      ) : (
        <Grid xs={12} md={6} item>
          <Input
            type="text"
            name="receiverRegisterNumber"
            label="Nr. de inregistrare al destinatarului"
            fullWidth
            variant="outlined"
            value={currentCompany?.tradeRegisterNumber}
            onChange={event => {}}
            required
            disabled
          />
        </Grid>
      )}

      <Grid xs={12} md={6} item>
        <AsyncAutocomplete
          variant="outlined"
          name="operationTypes"
          label="Tip operatie (selectie multipla)"
          value={formik.values.operationTypes}
          options={operationTypesOptions || []}
          onChange={value => formik.setFieldValue('operationTypes', value)}
          error={!!formik.errors.operationTypes}
          helperText={formik.errors.operationTypes as string}
          required
          multiple
        />
      </Grid>
      <Grid xs={12} md={6} item>
        <Input
          type="number"
          name="wasteCollectionCompanyReceivedWeight"
          label="Cantitatea receptionata (kg)"
          fullWidth
          variant="outlined"
          value={formik.values.wasteCollectionCompanyReceivedWeight}
          {...getNonNegativeNumberInputHandlers(
            formik.setFieldValue,
            'wasteCollectionCompanyReceivedWeight'
          )}
          inputProps={{ min: 0 }}
          error={!!formik.errors.wasteCollectionCompanyReceivedWeight}
          helperText={formik.errors.wasteCollectionCompanyReceivedWeight as string}
        />
      </Grid>
      <Grid xs={12} md={6} item>
        <Input
          type="number"
          name="wasteCollectionCompanyRejectedWeight"
          label="Cantitatea respinsa (kg)"
          fullWidth
          variant="outlined"
          value={formik.values.wasteCollectionCompanyRejectedWeight}
          {...getNonNegativeNumberInputHandlers(
            formik.setFieldValue,
            'wasteCollectionCompanyRejectedWeight'
          )}
          inputProps={{ min: 0 }}
          error={!!formik.errors.wasteCollectionCompanyRejectedWeight}
          helperText={formik.errors.wasteCollectionCompanyRejectedWeight as string}
        />
      </Grid>
      <Grid xs={12} md={6} item>
        <DatePicker
          label="Data primirii"
          onChange={value => formik.setFieldValue('wasteCollectionCompanyReceivedDate', value)}
          variant="outlined"
          helperText={formik.errors.wasteCollectionCompanyReceivedDate as string}
          error={!!formik.errors.wasteCollectionCompanyReceivedDate}
          value={dayjs(formik.values.wasteCollectionCompanyReceivedDate)}
          style={{ width: '100%' }}
        />
      </Grid>
      {!isExternalArticlesReport && (
        <Grid xs={12} md={12} item>
          <Input
            type="textarea"
            name="receiverName"
            label="Denumirea societatii, sediul, cod unic de identificare"
            variant="outlined"
            value={currentCompany?.description}
            onChange={event => {}}
            required
            width="100%"
            disabled
          />
        </Grid>
      )}
    </>
  )
}

export default DangerousDEEEReportForm
